import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import AutoModeOutlinedIcon from "@mui/icons-material/AutoModeOutlined";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  deleteById,
  findByTdpCodigo,
  generateByCronogramaDia,
  generateCreateCronogramaDia,
  generateCreateCronogramaDiaByTdp,
  generateCreateCronogramaDiaByTdpAndDate,
  generateCreateCronogramaDiaByTurma,
  save,
} from "./api-cronograma-quant-aula-bimestre";
import { errorMessage, successMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import LoadingDialog from "../../UI/LoadingDialog";
import TextFieldDate from "../../UI/TextFieldDate";
import TurmaDisciplinaProfessorProfile from "../../TDP/TurmaDisciplinaProfessorProfile";
import TDPTableSelect from "../../TDP/TDPTableSelect";
import { mensagemDe } from "../cronogramaUtil";
import CronogramaQuantAulaBimestreForm from "./CronogramaQuantAulaBimestreForm";
import CronogramaQuantAulaBimestreTable from "./CronogramaQuantAulaBimestreTable";

const porBimestre = (a, b) =>
  (a.bimestre?.anoLetivo ?? 0) - (b.bimestre?.anoLetivo ?? 0) ||
  (a.bimestre?.numeroBimestre ?? 0) - (b.bimestre?.numeroBimestre ?? 0);

const ordenar = (lista) => [...lista].sort(porBimestre);

const confirmar = (message, onConfirm) =>
  confirmAlert({
    title: "Alerta",
    message,
    buttons: [
      { label: "Sim", onClick: onConfirm },
      { label: "Não", onClick: () => {} },
    ],
  });

/** Bloco de geração: título, descrição e botão de ação. */
const AcaoGerar = ({ titulo, descricao, onClick, disabled, children }) => (
  <Box
    sx={{
      p: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
    }}
  >
    <Box flexGrow={1}>
      <Typography variant="subtitle2">{titulo}</Typography>
      <Typography variant="body2" color="text.secondary">
        {descricao}
      </Typography>
    </Box>
    {children}
    <Button variant="outlined" startIcon={<AutoModeOutlinedIcon />} onClick={onClick} disabled={disabled}>
      Gerar
    </Button>
  </Box>
);

const CronogramaQuantAulaBimestreContainer = () => {
  const [tdp, setTdp] = useState(null);
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dateGenerate, setDateGenerate] = useState(null);
  const [reload, setReload] = useState(0);
  const codTdp = tdp?.codigo;

  useEffect(() => {
    if (!codTdp) {
      setList([]);
      return;
    }
    let active = true;
    setIsLoading(true);
    findByTdpCodigo(codTdp)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data, "Não foi possível carregar as quantidades de aulas."));
        } else if (Array.isArray(data)) {
          setList(ordenar(data));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codTdp, reload]);

  /** Confirma, executa a geração com o diálogo de espera e recarrega a lista. */
  const gerar = (mensagem, request) =>
    confirmar(mensagem, () => {
      setIsGenerating(true);
      request()
        .then((data) => {
          if (!data || data.error) {
            errorMessage(mensagemDe(data, "Não foi possível gerar as quantidades de aulas."));
            return;
          }
          successMessage("Quantidades geradas com sucesso.");
          setReload((n) => n + 1);
        })
        .finally(() => setIsGenerating(false));
    });

  const handleSave = (persist) => {
    const eNovo = !persist.codigo;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(mensagemDe(data, "Não foi possível salvar a quantidade de aulas."));
          return;
        }
        setList((prevState) =>
          ordenar(eNovo ? [...prevState, data] : prevState.map((item) => (item.codigo === data.codigo ? data : item))),
        );
        setEditando(null);
        success("Registro salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = (item) =>
    confirmar(`Deseja remover a quantidade de aulas do ${item.bimestre?.numeroBimestre}º bimestre?`, () => {
      deleteById(item.codigo).then((data) => {
        if (data?.error) {
          errorMessage(mensagemDe(data, "Não foi possível remover a quantidade de aulas."));
          return;
        }
        setList((prevState) => prevState.filter((c) => c.codigo !== item.codigo));
        success("Registro removido.");
      });
    });

  const handleTdpSelected = (novo) => {
    setTdp(novo);
    setEditando(null);
    setDateGenerate(null);
  };

  const acoesTurma = tdp
    ? [
        {
          titulo: "Turma - gerar e criar cronograma dia",
          descricao: "Cria o cronograma dia e gera as quantidades de todas as disciplinas da turma.",
          onClick: () =>
            gerar("Deseja gerar todas as quantidades de aulas da turma?", () =>
              generateCreateCronogramaDiaByTurma(tdp.turma.codigo),
            ),
        },
        {
          titulo: "Turma - a partir do cronograma dia",
          descricao: "Gera as quantidades da turma usando o cronograma dia já existente.",
          onClick: () =>
            gerar("Deseja gerar todas as quantidades de aulas da turma a partir do cronograma dia?", () =>
              generateByCronogramaDia(tdp.turma.codigo),
            ),
        },
        {
          titulo: "Turma/Disciplina - todos",
          descricao: "Cria o cronograma dia e gera as quantidades somente desta turma - disciplina.",
          onClick: () =>
            gerar("Deseja gerar todas as quantidades de aulas da turma - disciplina?", () =>
              generateCreateCronogramaDiaByTdp(tdp.codigo),
            ),
        },
      ]
    : [];

  return (
    <Container>
      <LoadingDialog
        openDialog={isGenerating}
        setOpenDialog={setIsGenerating}
        title="Esta ação pode levar alguns minutos"
        description="Gerando quantidades de aulas..."
      />
      <CronogramaQuantAulaBimestreForm
        cqab={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Box mb={3}>
        <Typography variant="h6">Quantidade de Aulas por Bimestre</Typography>
        <Typography variant="body2" color="text.secondary">
          Aulas planejadas por bimestre de cada turma - disciplina, geradas a partir do cronograma.
        </Typography>
      </Box>

      {!tdp ? (
        <Stack spacing={3}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}
          >
            <Box>
              <Typography variant="subtitle2">Toda a instituição</Typography>
              <Typography variant="body2" color="text.secondary">
                Cria o cronograma dia e gera as quantidades de aulas de todas as turmas do ano letivo.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<AutoModeOutlinedIcon />}
              sx={{ flexShrink: 0 }}
              onClick={() =>
                gerar("Deseja gerar todas as quantidades de aulas da instituição?", () => generateCreateCronogramaDia())
              }
            >
              Gerar
            </Button>
          </Stack>
          <Box>
            <Typography variant="subtitle2" mb={1}>
              Selecione uma turma - disciplina
            </Typography>
            <TDPTableSelect setTdp={handleTdpSelected} />
          </Box>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <TurmaDisciplinaProfessorProfile tdp={tdp} onTdpSelected={handleTdpSelected} />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              Gerar quantidades
            </Typography>
            <Grid container spacing={2}>
              {acoesTurma.map((acao) => (
                <Grid key={acao.titulo} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <AcaoGerar {...acao} />
                </Grid>
              ))}
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <AcaoGerar
                  titulo="Turma/Disciplina - a partir da data"
                  descricao="Gera as quantidades desta turma - disciplina a partir da data informada."
                  disabled={!dateGenerate}
                  onClick={() =>
                    gerar("Deseja gerar as quantidades de aulas da turma - disciplina a partir da data?", () =>
                      generateCreateCronogramaDiaByTdpAndDate(tdp.codigo, dateGenerate),
                    )
                  }
                >
                  <TextFieldDate
                    label="Data"
                    value={dateGenerate}
                    onChange={setDateGenerate}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                </AcaoGerar>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                Quantidades cadastradas
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditando({ eNovo: true, tdp })}>
                Novo Registro
              </Button>
            </Stack>
            <CronogramaQuantAulaBimestreTable
              list={list}
              isLoading={isLoading}
              onEdit={(item) => setEditando({ ...item, tdp: item.tdp ?? tdp })}
              onDelete={handleDelete}
            />
          </Box>
        </Stack>
      )}
    </Container>
  );
};

export default CronogramaQuantAulaBimestreContainer;
